/**
 * ExpertClinicalNotesDB Component
 * 
 * This is the main component for managing clinical notes in the expert dashboard.
 * It handles both the default view (list of clients) and the detailed view (clinical note editor).
 * Features include:
 * - WebSocket-based real-time collaboration
 * - Clinical note creation, editing, saving, and submission
 * - Multi-tab interface for different note sections
 * - Client management and pagination
 * - PDF generation and export
 * - Session management and authentication
 */

// React core imports
import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import ReconnectingWebSocket from "reconnecting-websocket";

// Application context and authentication
import { AppContext } from "../../context";
import { useAuth } from "../../auth";

// Clinical notes components - UI panels and forms
import UserInfoPanel from "./clinicalNotesClinicalRoute/Components/UserInfoPanel";
import UserSummaryPanel from "./clinicalNotesClinicalRoute/Components/UserSummaryPanel";
import CNSessionNote from "./clinicalNotesClinicalRoute/CNSessionNote";
import CNPrescription from "./clinicalNotesClinicalRoute/CNPrescription";
import CNFollowUpSummary from "./clinicalNotesClinicalRoute/CNFollowUpSummary";
import CNCaseHistory from "./clinicalNotesClinicalRoute/CNCaseHistory";
import CNRecommendation from "./clinicalNotesClinicalRoute/CNRecommendation";
import CNSessionTranscript from "./clinicalNotesClinicalRoute/CNSessionTranscript";

// Clinical notes components - UI elements and dialogs
import ClientCardsSkeleton from "./clinicalNotesClinicalRoute/Skeletons/ClientCardsSkeleton";
import ClientsCard from "./clinicalNotesClinicalRoute/Components/ClientsCard";
import ClinicalNoteStatus from "./clinicalNotesClinicalRoute/Components/ClinicalNoteStatus";
import CreateClinicalNote from "./clinicalNotesClinicalRoute/Dialogs/CreateClinicalNote";
import FloatingService from "./clinicalNotesClinicalRoute/AssistantComponents/FloatingService";
import ClinicalNoteSubmitDialog from "./clinicalNotesClinicalRoute/Dialogs/ClinicalNoteSubmitDialog";
import PDFDialog from "./clinicalNotesClinicalRoute/Dialogs/PDFDialog";
import SaveUnsavedChanges from "./clinicalNotesClinicalRoute/Components/SaveUnsavedChanges";
import AuditLogs from "./clinicalNotesClinicalRoute/Components/AuditLogs";
import Actions from "./clinicalNotesClinicalRoute/Components/Actions";

// API functions for clinical notes management
import {
  DeleteClinicalNotesClient,
  FetchArchivedClinicalNotesClients,
  FetchClinicalNotesClientDetails,
  FetchClinicalNotesClients, //Current Client List
  FetchFollowUpNote,
  FetchSummaryFromRAG,
  InsertRagSessionData,
  onboardingTemplate,
} from "./clinicalNotesClinicalRoute/ClinicalNotesAPI/ClinicalNoteAPIManager";

// Material-UI components
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Pagination from "@mui/material/Pagination";
import {
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Menu,
  PaginationItem,
  Typography,
} from "@mui/material";

// Material-UI icons
import {
  Download,
  MenuOutlined,
  SaveOutlined,
  SendOutlined,
} from "@mui/icons-material";

// Other utilities and assets
import { createNoteSVG } from "./clinicalNotesClinicalRoute/ClinicalNoteSVGs";
import { set } from "date-fns";
import jwtDecode from "jwt-decode";

/**
 * CustomTabPanel Component
 * 
 * A reusable tab panel component that conditionally renders its children
 * based on whether the current tab value matches the panel's index.
 * Provides proper accessibility attributes for screen readers.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to render when tab is active
 * @param {number} props.value - Currently selected tab index
 * @param {number} props.index - This panel's tab index
 * @param {Object} props.other - Additional props passed to the container div
 */
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

// PropTypes validation for CustomTabPanel
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

/**
 * ExpertClinicalNotesDB Component
 * 
 * Main component for the clinical notes dashboard that manages:
 * - Two views: default (client list) and view (clinical note editor)
 * - Real-time WebSocket communication for collaborative editing
 * - Clinical note CRUD operations (Create, Read, Update, Delete)
 * - Session management and state persistence
 * - Multi-tab interface for different clinical note sections
 * - PDF export functionality
 * - Client pagination and filtering
 */
const ExpertClinicalNotesDB = () => {
  // Context and global state management
  const {
    expertClinicalNotesData,      // Main clinical notes data object
    setExpertClinicalNotesData,   // Function to update clinical notes data
    setSavedCopy,                 // Function to save a copy of current state
    saveChanges,                  // Function to trigger save changes
  } = useContext(AppContext);

  // Component state variables
  const [clinicalNotesState, setClinicalNotesState] = useState({
    view: "default", // Controls main view: "default" (client list) or "view" (note editor)
  });
  const [session, setSession] = useState("In Progress"); // Current session filter: "In Progress", "Archived", etc.
  const [selectedTab, setSelectedTab] = useState(0); // Currently active tab in the note editor
  const [appointmentIDfromAPI, setAppointmentIDfromAPI] = useState(); // ID of the current appointment/clinical note
  const [clientsList, setClientsList] = useState([]); // Array of clients for the default view
  const [loading, setLoading] = useState(true); // Loading state for API calls
  const [submission, setSubmission] = useState(false); // Controls submit dialog visibility
  const [summaryLoading, setSummaryLoading] = useState(false); // Loading state for summary generation
  const [authorized, setAuthorized] = useState({ view: true, message: "" }); // Authorization status and message
  const [tempData, setTempData] = useState({}); // Temporary data for real-time updates via WebSocket
  const [page, setPage] = useState(1); // Current pagination page
  const [totalPages, setTotalPages] = useState(0); // Total number of pages for pagination
  const [pdfView, setPdfView] = useState(false); // Controls PDF dialog visibility
  const [noteType, setNoteType] = useState(""); // Type of clinical note being created/viewed

  // React hooks and refs
  const socketRef = useRef(null); // WebSocket connection reference for real-time updates
  const navigate = useNavigate(); // React Router navigation function
  const location = useLocation(); // Current location object from React Router
  const { enqueueSnackbar } = useSnackbar(); // Notification system
  const { currentUser } = useAuth(); // Current authenticated user

  // Local storage and authentication
  const storedData = localStorage.getItem("Authorization"); // JWT token from localStorage
  const professional_id = JSON.parse(localStorage.getItem("profile"))?.UserId; // Current professional's ID

  // WebSocket and connection management variables
  let FIRST_LOAD = false; // Flag to track if this is the initial data load
  let pingInterval; // Interval ID for WebSocket ping messages
  let pingTime = 1; // Ping interval multiplier (8 seconds * pingTime)

  // Configuration constants
  const restricteddownload = ["US", "AU"]; // Countries with download restrictions
  /**
   * checkAndActOnIsEdited - Utility function for handling edited data detection
   * 
   * This function recursively traverses an object to find properties marked with "is_edited": "true"
   * and merges those changes into the updatedData object. This is used to track and apply
   * real-time edits received via WebSocket.
   * 
   * @param {Object} obj - The source object to check for edited properties
   * @param {Object} updatedData - The target object to merge edited properties into
   * @returns {Object} The updated data object with merged changes
   */
  const checkAndActOnIsEdited = (obj, updatedData) => {
    /**
     * Recursive inner function that performs the actual traversal and merging
     * 
     * @param {Object} obj - Current object being examined
     * @param {Object} updatedData - Current target object for merging
     */
    function checkForIsEdited(obj, updatedData) {
      // Base case: if obj is not an object or is null, stop traversal
      if (typeof obj !== "object" || obj === null) {
        return;
      }

      // Iterate through all properties in the current object
      for (const key in obj) {
        if (!obj.hasOwnProperty(key)) continue; // Skip inherited properties

        // If we find an "is_edited" flag set to "true", copy all properties from this level
        if (key === "is_edited" && obj[key] === "true") {
          for (const k in obj) {
            if (obj.hasOwnProperty(k)) {
              updatedData[k] = obj[k]; // Copy all properties at this level
            }
          }
        }
        // If the property is an object, recursively check it
        else if (typeof obj[key] === "object" && obj[key] !== null) {
          // Ensure the corresponding key exists in updatedData
          if (!updatedData[key]) {
            updatedData[key] = Array.isArray(obj[key]) ? [] : {};
          }
          checkForIsEdited(obj[key], updatedData[key]); // Recursive call
        }
        // Copy non-object properties if they don't already exist in updatedData
        else if (!updatedData.hasOwnProperty(key)) {
          updatedData[key] = obj[key];
        }
      }
    }

    // Start the recursive traversal
    checkForIsEdited(obj, updatedData);

    return updatedData;
  };

  /**
   * useEffect: Handle real-time data updates from WebSocket
   * 
   * This effect is triggered whenever tempData changes (from WebSocket messages).
   * It processes the changes, updates the global state, and sends the updated
   * data back through the WebSocket for real-time collaboration.
   * 
   * Dependencies: [tempData] - Runs when temporary data from WebSocket changes
   */
  useEffect(() => {
    // Process the received data to merge any edited properties
    const newData = checkAndActOnIsEdited(
      expertClinicalNotesData?.data,
      tempData
    );

    // Update the global clinical notes data with the processed changes
    setExpertClinicalNotesData((prev) => {
      return {
        ...prev,
        data: newData,
      };
    });

    // Prepare the complete data payload for WebSocket transmission
    const data = JSON.stringify({
      header: expertClinicalNotesData?.headerData,
      session_data: {
        ...newData,
        recommendation: expertClinicalNotesData?.recommendation,
      },
    });

    // Send the updated data through WebSocket if connection exists
    if (socketRef.current) {
      socketRef.current.send(data);
    }
  }, [tempData]);

  /**
   * useEffect: Handle URL parameter changes for view routing
   * 
   * Monitors URL search parameters to determine the current view state.
   * When URL contains view=view&clinicalId=X, switches to clinical note editor.
   * Otherwise, defaults to the client list view.
   * 
   * Dependencies: [location.search] - Runs when URL search parameters change
   */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const viewParam = queryParams.get("view");
    const clinicalIdParam = queryParams.get("clinicalId");

    // If URL contains view=view and a clinical ID, switch to editor view
    if (viewParam === "view" && clinicalIdParam) {
      setClinicalNotesState({ view: viewParam });
      setAppointmentIDfromAPI(clinicalIdParam);
      setNoteType(queryParams.get("noteType"));
    } else {
      // Default to client list view
      setClinicalNotesState({ view: "default" });
      setAppointmentIDfromAPI(null);
      setNoteType("");
    }
  }, [location.search]);

  /**
   * useEffect: Handle view changes and navigation
   * 
   * Manages the transition between views and cleans up state when switching.
   * - When switching to "view" mode: Updates URL with clinical note parameters
   * - When switching to "default" mode: Resets all clinical note data and closes WebSocket
   * 
   * Dependencies: [clinicalNotesState?.view, appointmentIDfromAPI] - Runs when view or appointment ID changes
   */
  useEffect(() => {
    if (clinicalNotesState.view === "view") {
      // Navigate to the clinical note editor with URL parameters
      const newURL = `/clinical/home?view=${clinicalNotesState.view}&clinicalId=${appointmentIDfromAPI}&noteType=${noteType}`;
      navigate(newURL);
    } else {
      // Reset all state when returning to default view
      setExpertClinicalNotesData(null);
      setSavedCopy({
        progress_note: {},
        prescription: {},
        follow_up_summary: {},
        case_history: {},
        summary: {},
        lab_test: {},
        recommendation: {},
        list: new Set(),
      });
      setAuthorized({ view: true, message: "" });
      // Close WebSocket connection if it exists
      socketRef?.current?.close();
    }
  }, [clinicalNotesState?.view, appointmentIDfromAPI]);

  /**
   * useEffect: Handle data fetching based on session type and pagination
   * 
   * Fetches the appropriate client list based on the current session filter.
   * - "Archived" session: Fetches archived clinical notes
   * - Other sessions: Fetches active clinical notes
   * 
   * Dependencies: [session, clinicalNotesState?.view, page] - Runs when session filter, view, or page changes
   */
  useEffect(() => {
    if (session === "Archived") {
      fetchArchivedClinicalNotesList();
    } else {
      fetchClinicalNotesList();
    }
  }, [session, clinicalNotesState?.view, page]);

  /**
   * useEffect: Initialize clinical note view and WebSocket connection
   * 
   * This is the main effect that handles:
   * 1. Loading client details and templates when entering clinical note view
   * 2. Setting up WebSocket connection for real-time collaboration
   * 3. Handling WebSocket message parsing and state updates
   * 4. Managing authorization and error states
   * 
   * Dependencies: [clinicalNotesState?.view] - Runs when view changes
   */
  useEffect(() => {
    // Early return for default view - reset state and exit
    if (clinicalNotesState.view === "default") {
      setExpertClinicalNotesData({});
      setSavedCopy({
        progress_note: {},
        prescription: {},
        follow_up_summary: {},
        case_history: {},
        summary: {},
        lab_test: {},
        recommendation: {},
        list: new Set(),
      });
      return;
    }

    // Initialize variables for client info and templates
    let clientInfo = {};
    let templates;
    let tabs;

    // Start the initialization process by fetching client details
    FetchClinicalNotesClientDetails(appointmentIDfromAPI).then((res) => {
      // Handle authorization errors
      if (res?.error) {
        setAuthorized({
          view: false,
          message:
            " Sorry, you don't have permission to view these details. If you believe this is an error, please contact support. We appreciate your understanding.",
        });
        return;
      }

      // Decode the client profile data from JWT
      const profileData = jwtDecode(res);

      // Fetch onboarding templates and tabs configuration
      onboardingTemplate(currentUser?.UserId).then((resp) => {
        if (resp.ResponseCode === 200) {
          templates = resp?.template;
          tabs = resp?.tabs;
        } else {
          enqueueSnackbar(resp?.ResponseMessage, { variant: "error" });
          return;
        }
      });

      // Set authorization status and update clinical notes data with client info
      setAuthorized({ view: true, message: "" });
      setExpertClinicalNotesData((prevData) => ({
        ...prevData,
        client: profileData,
        data: {
          ...prevData?.data,
          client: profileData,
        },
      }));
      clientInfo = profileData;
      FIRST_LOAD = true;

      // Initialize WebSocket connection for real-time collaboration
      socketRef.current = new ReconnectingWebSocket(
        `${process.env.REACT_APP_EXPERT_BASE_WEBSOCKET_URL}ws?authorization=${storedData?.split("Bearer ")[1]
        }&appointment_id=${appointmentIDfromAPI}`
      );
      // WebSocket Event Handler: Connection Opened
      socketRef.current.addEventListener("open", (event) => {
        console.log("WebSocket connection opened!");
        // Send initial ping to establish connection
        socketRef.current.send("ping");
        // Set up ping interval to keep connection alive (every 8 seconds)
        pingInterval = setInterval(() => {
          socketRef.current.send("ping");
        }, pingTime * 8000);
        socketRef.current.pingInterval = pingInterval;
      });

      // WebSocket Event Handler: Message Received
      socketRef.current.addEventListener("message", (event) => {
        console.log("Message received from server!");

        try {
          // Parse the incoming WebSocket message
          let resp_data = JSON.parse(event.data);

          const data = resp_data?.session_data;
          const headerData = resp_data?.header;

          // Process and clean the received data
          const updatedData = {
            ...data,
            // Clean up progress note data
            progress_note: {
              ...data?.progress_note,
              // Ensure follow_up_date is a string
              follow_up_date:
                typeof data?.progress_note?.follow_up_date === "string"
                  ? data?.progress_note?.follow_up_date
                  : "",
              // Filter out empty/null medicines
              medicines: {
                ...data?.progress_note?.medicines,
                prescribed_medicines:
                  data?.progress_note?.medicines?.prescribed_medicines?.filter(
                    (item) =>
                      item?.drug !== "" &&
                      item?.drug != null &&
                      item?.drug !== undefined
                  ),
              },
            },
            // Filter out empty/null lab tests
            lab_test: {
              ...data?.lab_test,
              prescribed_lab_test: data?.lab_test?.prescribed_lab_test?.filter(
                (item) =>
                  item?.test_name !== "" &&
                  item?.test_name != null &&
                  item?.test_name !== undefined
              ),
            },
            // Use received templates or fall back to defaults
            template:
              resp_data?.session_data?.template?.length > 0
                ? resp_data?.session_data?.template
                : templates,
            tab:
              resp_data?.session_data?.tab?.length > 0
                ? resp_data?.session_data?.tab
                : tabs,
            client: clientInfo,
            updated: "false",
          };
          // Handle initial data load
          if (FIRST_LOAD) {
            // Set the complete clinical notes data for the first time
            setExpertClinicalNotesData((prevData) => ({
              ...prevData,
              data: {
                ...updatedData,
              },
              headerData: headerData,
              recommendation: updatedData?.recommendation,
              socketRef: socketRef,
              template:
                resp_data?.session_data?.template?.length > 0
                  ? resp_data?.session_data?.template
                  : templates,
              tab:
                resp_data?.session_data?.tab?.length > 0
                  ? resp_data?.session_data?.tab
                  : tabs,
            }));

            // Send acknowledgment back to server with the processed data
            socketRef.current.send(
              JSON.stringify({
                header: headerData,
                session_data: {
                  ...updatedData,
                },
              })
            );

            // Initialize the saved copy for change tracking
            setSavedCopy((prev) => ({
              ...prev,
              progress_note: updatedData?.progress_note,
              prescription: updatedData?.prescription,
              follow_up_summary: updatedData?.follow_up_summary,
              case_history: updatedData?.case_history,
              summary: updatedData?.summary,
              lab_test: updatedData?.lab_test?.prescribed_lab_test,
              recommendation: updatedData?.recommendation,
            }));
            FIRST_LOAD = false;
          }

          // Handle real-time updates from other users
          if (data?.updated?.toLowerCase() === "true") {
            setTempData(updatedData);
          }
        } catch (error) {
          // Handle JSON parsing errors or other message processing errors
          console.error("Error parsing WebSocket message:", error);
        }
      });

      // WebSocket Event Handler: Connection Closed
      socketRef.current.addEventListener("close", (event) => {
        console.log("WebSocket connection closed!");
        // Clean up ping interval when connection closes
        if (socketRef.current.pingInterval) {
          clearInterval(socketRef.current.pingInterval);
          socketRef.current.pingInterval = null;
        }
      });
    });
  }, [clinicalNotesState?.view]);

  /**
   * useEffect: Initialize RAG session and fetch follow-up notes
   * 
   * When a clinical note session is established (user_id is available):
   * 1. Sends initial session data to RAG (Retrieval-Augmented Generation) system
   * 2. Fetches historical follow-up notes for the client
   * 3. Updates clinical notes data with expert info and follow-up history
   * 
   * Dependencies: [expertClinicalNotesData?.headerData?.user_id] - Runs when user ID becomes available
   */
  useEffect(() => {
    // Skip if not in clinical note view or user ID not available
    if (
      clinicalNotesState.view === "default" ||
      !expertClinicalNotesData?.headerData?.user_id
    ) {
      return;
    }

    // Prepare session data for RAG system (without assistant data)
    const data = JSON.stringify({
      header: expertClinicalNotesData?.headerData,
      session_data: { ...expertClinicalNotesData?.data, assistant: {} },
    });

    // Send session data to RAG system for AI-powered insights
    InsertRagSessionData(data).then((res) => {
      if (res?.error) {
        enqueueSnackbar(res?.message, { variant: "info" });
        return;
      }
      console.log("Data sent to server");
    });

    // Get current expert information from localStorage
    let expert_info = JSON.parse(window.localStorage.getItem("profile"));

    // Fetch historical follow-up notes for this client (last 10)
    FetchFollowUpNote(expertClinicalNotesData?.headerData?.user_id, 10).then(
      (res) => {
        if (res?.error) {
          enqueueSnackbar(res?.message, { variant: "info" });
          return;
        }

        // Update clinical notes data with expert info and follow-up history
        setExpertClinicalNotesData((prevData) => ({
          ...prevData,
          expert_info: expert_info,
          followUpNotes: jwtDecode(res),
        }));
      }
    );
  }, [expertClinicalNotesData?.headerData?.user_id]);

  /**
   * fetchClinicalNotesList - Fetch active clinical notes clients
   * 
   * Retrieves a paginated list of clients with active clinical notes
   * for the current professional. Updates the clients list and pagination state.
   * Also fetches detailed client information to get phone numbers.
   */
  const fetchClinicalNotesList = async () => {
    // Skip if currently viewing a specific clinical note
    if (clinicalNotesState.view === "view") {
      return;
    }

    try {
      // Ensure professional ID is available
      if (!professional_id) return;

      setLoading(true);

      // Fetch clients with clinical notes for current professional
      const clients = await FetchClinicalNotesClients(
        professional_id,
        session,
        page
      );

      // Handle API errors
      if (clients?.error) {
        enqueueSnackbar(clients?.message, { variant: "info" });
        setLoading(false);
        return;
      }

      // Decode the client data from JWT
      const clientData = jwtDecode(clients?.data);
      //console.log("clientData ========>", clientData)
      const filteredClientData = clientData.filter((item) => !item.client.phone || item.client.phone == "")
     // console.log("filteredClientData ========>", filteredClientData)
      // Fetch detailed information for each client to get phone numbers
      const enrichedClientData = await Promise.allSettled(
        clientData.map(async (client) => {
          try {
            const clientDetails = await FetchClinicalNotesClientDetails(client._id);

            if (clientDetails?.error) {
              // If we can't get details for this client, return original data
              return client;
            }

            // Decode the detailed client data from JWT
            const detailedData = jwtDecode(clientDetails);

            // Update the client object with phone information
            return {
              ...client,
              client: {
                ...client.client,
                phone: detailedData.phone || client.client.phone || ""
              }
            };
          } catch (error) {
            console.log("Error fetching details for client:", client._id, error);
            // Return original client data if there's an error
            return client;
          }
        })
      );

      // Extract successful results and handle any rejected promises
      const finalClientData = enrichedClientData.map((result) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          console.log("Promise rejected:", result.reason);
          // Return null for failed requests, we'll filter these out
          return null;
        }
      }).filter(Boolean); // Remove null values

      // Update pagination and clients list
      setTotalPages(clients?.total_pages);
      setClientsList(finalClientData);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };
  /**
   * fetchArchivedClinicalNotesList - Fetch archived clinical notes clients
   * 
   * Retrieves a paginated list of clients with archived clinical notes
   * for the current professional. Similar to fetchClinicalNotesList but for archived data.
   */
  const fetchArchivedClinicalNotesList = async () => {
    // Skip if currently viewing a specific clinical note
    if (clinicalNotesState.view === "view") {
      return;
    }

    setLoading(true);
    try {
      // Fetch archived clients with clinical notes
      const clients = await FetchArchivedClinicalNotesClients(
        professional_id,
        session,
        page
      );

      // Handle API errors
      if (clients?.error) {
        enqueueSnackbar(clients?.message, { variant: "info" });
        return;
      }

      // Decode the client data from JWT
      const clientData = jwtDecode(clients?.data);

      // Update pagination and clients list
      setTotalPages(clients?.total_pages);
      setClientsList(clientData);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  /**
   * fetchSummary - Generate AI-powered clinical summary
   * 
   * Initiates the AI summary generation process by:
   * 1. Sending current session data to WebSocket and RAG system
   * 2. Waiting for AI processing (4 second delay)
   * 3. Fetching the generated summary from RAG system
   * 4. Updating the clinical notes data with the summary
   * 5. Broadcasting the update via WebSocket
   */
  const fetchSummary = async () => {
    setSummaryLoading(true);

    // Prepare session data for WebSocket transmission
    const data = JSON.stringify({
      header: expertClinicalNotesData?.headerData,
      session_data: {
        ...expertClinicalNotesData?.data,
        recommendation: expertClinicalNotesData?.recommendation,
      },
    });

    // Prepare session data for RAG system (without assistant data)
    const rag_data = JSON.stringify({
      header: expertClinicalNotesData?.headerData,
      session_data: {
        ...expertClinicalNotesData?.data,
        assistant: {},
        recommendation: expertClinicalNotesData?.recommendation,
      },
    });

    // Send current data via WebSocket for real-time updates
    if (socketRef.current) {
      socketRef.current.send(data);
    }

    // Send data to RAG system for AI processing
    InsertRagSessionData(rag_data).then((res) => {
      // Wait 4 seconds for AI processing to complete
      setTimeout(() => {
        // Fetch the generated summary from RAG system
        FetchSummaryFromRAG(
          expertClinicalNotesData?.headerData?.user_id,
          expertClinicalNotesData?.headerData?.session_id
        )
          .then((res) => {
            // Handle API errors
            if (res?.error) {
              enqueueSnackbar(res?.message, { variant: "info" });
              setSummaryLoading(false);
              return;
            }

            // Update clinical notes data with generated summary
            setExpertClinicalNotesData((prevData) => ({
              ...prevData,
              data: {
                ...prevData.data,
                summary: res,
              },
            }));

            // Prepare updated data with summary for WebSocket
            const data = JSON.stringify({
              header: expertClinicalNotesData?.headerData,
              session_data: {
                ...expertClinicalNotesData?.data,
                summary: res,
              },
            });

            // Broadcast the summary update via WebSocket
            if (socketRef.current) {
              socketRef.current.send(data);
            }
            setSummaryLoading(false);
          })
          .catch((err) => {
            console.error(err);
            setSummaryLoading(false);
          });
      }, 4000);
    });
  };

  /**
   * handleChangeTab - Handle tab navigation in clinical note editor
   * 
   * @param {Event} event - The tab change event
   * @param {number} newValue - Index of the newly selected tab
   */
  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  /**
   * recommendationHandlerOnSave - Handle recommendation notifications on save
   * 
   * Compares previous and new recommendations to identify newly added items
   * and sends WebSocket notifications for each new recommendation.
   * Handles experts, programs, assessments, and content recommendations.
   */
  const recommendationHandlerOnSave = () => {
    const previouseRecommendation =
      expertClinicalNotesData?.data?.recommendation;
    const newRecommendation = expertClinicalNotesData?.recommendation;
    const existingExperts = previouseRecommendation?.recommended_expert?.map(
      (item) => item?.Name
    );
    newRecommendation?.recommended_expert?.map((item) => {
      if (!existingExperts?.includes(item?.Name)) {
        expertClinicalNotesData.socketRef.current.send(
          JSON.stringify({
            action_type: "recommended",
            details: {
              recommendation: item?.Name,
            },
            user_id: expertClinicalNotesData?.headerData?.user_id,
            expert_id: expertClinicalNotesData?.headerData?.expert_id,
          })
        );
      }
    });
    const existingPrograms = previouseRecommendation?.recommended_program?.map(
      (item) => item?.name
    );
    console.log(
      existingPrograms,
      "existingPrograms",
      newRecommendation?.recommended_program
    );

    newRecommendation?.recommended_program?.map((item) => {
      if (!existingPrograms?.includes(item?.name)) {
        expertClinicalNotesData.socketRef.current.send(
          JSON.stringify({
            action_type: "recommended",
            details: {
              recommendation: item?.name,
            },
            user_id: expertClinicalNotesData?.headerData?.user_id,
            expert_id: expertClinicalNotesData?.headerData?.expert_id,
          })
        );
      }
    });
    const existingAssesments =
      previouseRecommendation?.recommended_assessment?.map(
        (item) => item?.title
      );
    newRecommendation?.recommended_assessment?.map((item) => {
      if (!existingAssesments?.includes(item?.title)) {
        expertClinicalNotesData.socketRef.current.send(
          JSON.stringify({
            action_type: "recommended",
            details: {
              recommendation: item?.title,
            },
            user_id: expertClinicalNotesData?.headerData?.user_id,
            expert_id: expertClinicalNotesData?.headerData?.expert_id,
          })
        );
      }
    });
    const existingContents = previouseRecommendation?.recommended_content?.map(
      (item) => item?.data?.title
    );
    newRecommendation?.recommended_content?.map((item) => {
      if (!existingContents?.includes(item?.data?.title)) {
        expertClinicalNotesData.socketRef.current.send(
          JSON.stringify({
            action_type: "recommended",
            details: {
              recommendation: item?.data?.title,
            },
            user_id: expertClinicalNotesData?.headerData?.user_id,
            expert_id: expertClinicalNotesData?.headerData?.expert_id,
          })
        );
      }
    });
  };

  /**
   * saveSession - Save the current clinical note session
   * 
   * This function handles the complete save process:
   * 1. Filters out empty medicines and lab tests
   * 2. Sends data via WebSocket for real-time sync
   * 3. Sends data to RAG system for AI processing
   * 4. Updates saved copy for change tracking
   * 5. Sends notification events via WebSocket
   * 6. Refreshes follow-up notes after delay
   * 7. Shows success notification to user
   */
  const saveSession = () => {
    // Prepare cleaned session data for WebSocket (filter out empty entries)
    const data = JSON.stringify({
      header: expertClinicalNotesData?.headerData,
      session_data: {
        ...expertClinicalNotesData?.data,
        recommendation: expertClinicalNotesData?.recommendation,
        // Filter out empty/invalid medicines
        progress_note: {
          ...expertClinicalNotesData?.data?.progress_note,
          medicines: {
            ...expertClinicalNotesData?.data?.progress_note?.medicines,
            prescribed_medicines:
              expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.filter(
                (item) =>
                  item?.drug !== "" &&
                  item?.drug != null &&
                  item?.drug !== undefined
              ),
          },
        },
        // Filter out empty/invalid lab tests
        lab_test: {
          ...expertClinicalNotesData?.data?.lab_test,
          prescribed_lab_test:
            expertClinicalNotesData?.data?.lab_test?.prescribed_lab_test?.filter(
              (item) =>
                item?.test_name !== "" &&
                item?.test_name != null &&
                item?.test_name !== undefined
            ),
        },
      },
    });

    // Prepare cleaned session data for RAG system (same cleaning + no assistant data)
    const rag_data = JSON.stringify({
      header: expertClinicalNotesData?.headerData,
      session_data: {
        ...expertClinicalNotesData?.data,
        assistant: {}, // Empty assistant data for RAG
        recommendation: expertClinicalNotesData?.recommendation,
        // Filter out empty/invalid medicines
        progress_note: {
          ...expertClinicalNotesData?.data?.progress_note,
          medicines: {
            ...expertClinicalNotesData?.data?.progress_note?.medicines,
            prescribed_medicines:
              expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.filter(
                (item) =>
                  item?.drug !== "" &&
                  item?.drug != null &&
                  item?.drug !== undefined
              ),
          },
        },
        // Filter out empty/invalid lab tests
        lab_test: {
          ...expertClinicalNotesData?.data?.lab_test,
          prescribed_lab_test:
            expertClinicalNotesData?.data?.lab_test?.prescribed_lab_test?.filter(
              (item) =>
                item?.test_name !== "" &&
                item?.test_name != null &&
                item?.test_name !== undefined
            ),
        },
      },
    });

    // Update saved copy for change tracking
    setSavedCopy((prev) => ({
      ...prev,
      progress_note: expertClinicalNotesData?.data?.progress_note || {},
      prescription: expertClinicalNotesData?.data?.prescription || {},
      follow_up_summary: expertClinicalNotesData?.data?.follow_up_summary || {},
      case_history: expertClinicalNotesData?.data?.case_history || {},
      summary: expertClinicalNotesData?.data?.summary || {},
      lab_test:
        expertClinicalNotesData?.data?.lab_test?.prescribed_lab_test || {},
      recommendation: expertClinicalNotesData?.recommendation || undefined,
      list: new Set(),
    }));

    // Trigger save changes action
    saveChanges();

    // Handle recommendation notifications
    recommendationHandlerOnSave();

    // Send data via WebSocket and send notification event
    if (socketRef.current) {
      // Send the cleaned session data
      socketRef.current.send(data);

      // Send save notification event
      expertClinicalNotesData.socketRef.current.send(
        JSON.stringify({
          action_type: "note_saved",
          details: {
            note_type: noteType,
            patient_name: expertClinicalNotesData?.data?.client?.name,
          },
          user_id: expertClinicalNotesData?.headerData?.user_id,
          expert_id: expertClinicalNotesData?.headerData?.expert_id,
        })
      );
    }
    setExpertClinicalNotesData((prevData) => ({
      ...prevData,
      data: {
        ...expertClinicalNotesData?.data,
        recommendation: expertClinicalNotesData?.recommendation,
        progress_note: {
          ...expertClinicalNotesData?.data?.progress_note,
          medicines: {
            ...expertClinicalNotesData?.data?.progress_note?.medicines,
            prescribed_medicines:
              expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.filter(
                (item) =>
                  item?.drug !== "" &&
                  item?.drug != null &&
                  item?.drug !== undefined
              ),
          },
        },
        lab_test: {
          ...expertClinicalNotesData?.data?.lab_test,
          prescribed_lab_test:
            expertClinicalNotesData?.data?.lab_test?.prescribed_lab_test?.filter(
              (item) =>
                item?.test_name !== "" &&
                item?.test_name != null &&
                item?.test_name !== undefined
            ),
        },
      },
      askStellaConversation: "init",
    }));
    InsertRagSessionData(rag_data).then((res) => {
      if (res?.error) {
        enqueueSnackbar(res?.message, { variant: "info" });
        setExpertClinicalNotesData((prevData) => ({
          ...prevData,
          askStellaConversation: "mid",
        }));
        return;
      }
      console.log("Data sent to server");
      setExpertClinicalNotesData((prevData) => ({
        ...prevData,
        askStellaConversation: "end",
      }));
    });
    enqueueSnackbar("Your clinical note has been saved!", {
      variant: "success",
    });
    setTimeout(() => {
      FetchFollowUpNote(expertClinicalNotesData?.headerData?.user_id, 10)
        .then((res) => {
          if (res?.error) {
            enqueueSnackbar(res?.message, { variant: "info" });
            return;
          }
          setExpertClinicalNotesData((prevData) => ({
            ...prevData,
            followUpNotes: jwtDecode(res),
          }));
        })
        .catch((err) => {
          console.error(err); // Optionally log the error internally here as well
        });
    }, 3000);
  };
  const fetchGraphData = async ({ user_id, session_id, registered_id }) => {
    const response = await fetch('https://rag.uwc.world/fetch-graph-data', {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id,
        session_id,
        registered_id,
      }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return await response.json();
  };

  const handleSubmit = () => {
    const data = JSON.stringify({
      header: expertClinicalNotesData?.headerData,
      session_data: {
        ...expertClinicalNotesData?.data,
        progress_note: {
          ...expertClinicalNotesData?.data?.progress_note,
          medicines: {
            ...expertClinicalNotesData?.data?.progress_note?.medicines,
            prescribed_medicines:
              expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.filter(
                (item) =>
                  item?.drug !== "" &&
                  item?.drug != null &&
                  item?.drug !== undefined
              ),
          },
        },
        lab_test: {
          ...expertClinicalNotesData?.data?.lab_test,
          prescribed_lab_test:
            expertClinicalNotesData?.data?.lab_test?.prescribed_lab_test?.filter(
              (item) =>
                item?.test_name !== "" &&
                item?.test_name != null &&
                item?.test_name !== undefined
            ),
        },
        submitted: true,
      },
    });
    const rag_data = JSON.stringify({
      header: expertClinicalNotesData?.headerData,
      session_data: {
        ...expertClinicalNotesData?.data,
        assistant: {},
        progress_note: {
          ...expertClinicalNotesData?.data?.progress_note,
          medicines: {
            ...expertClinicalNotesData?.data?.progress_note?.medicines,
            prescribed_medicines:
              expertClinicalNotesData?.data?.progress_note?.medicines?.prescribed_medicines?.filter(
                (item) =>
                  item?.drug !== "" &&
                  item?.drug != null &&
                  item?.drug !== undefined
              ),
          },
        },
        lab_test: {
          ...expertClinicalNotesData?.data?.lab_test,
          prescribed_lab_test:
            expertClinicalNotesData?.data?.lab_test?.prescribed_lab_test?.filter(
              (item) =>
                item?.test_name !== "" &&
                item?.test_name != null &&
                item?.test_name !== undefined
            ),
        },
        submitted: true,
      },
    });
    setExpertClinicalNotesData((prevData) => ({
      ...prevData,
      data: {
        ...prevData?.data,
        submitted: true,
      },
    }));
    if (socketRef.current) {
      socketRef.current.send(data);
      expertClinicalNotesData.socketRef.current.send(
        JSON.stringify({
          action_type: "note_submitted",
          details: {
            note_type: noteType,
            patient_name: expertClinicalNotesData?.data?.client?.name,
          },
          user_id: expertClinicalNotesData?.headerData?.user_id,
          expert_id: expertClinicalNotesData?.headerData?.expert_id,
        })
      );
    }
    InsertRagSessionData(rag_data)
      .then((res) => {
        console.log("Data sent to server");
        enqueueSnackbar("Your note has been submitted successfully", {
          variant: "success",
        });
        setTimeout(() => {
          setClinicalNotesState((prevState) => ({
            ...prevState,
            view: "default",
          }));
        }, 2000);
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong", { variant: "error" });
        console.error(err); // Optionally log the error internally here as well
      });
    fetchGraphData({ user_id: expertClinicalNotesData?.headerData?.expert_id, session_id: expertClinicalNotesData?.headerData?.session_id, registered_id: expertClinicalNotesData?.headerData?.expert_id })
  };

  const handleDeleteClinicalNote = async (data) => {
    setLoading(true);
    DeleteClinicalNotesClient(data?._id)
      .then((res) => {
        if (res?.error) {
          enqueueSnackbar(res?.message, { variant: "info" });
          setLoading(false);
          return;
        }
        fetchClinicalNotesList();
        setLoading(false);
      })
      .catch((err) => {
        console.error(err); // Optionally log the error internally here as well
      });
  };
  const sendPing = () => {
    if (socketRef.current) {
      socketRef.current.send("ping");
    }
  };

  const handleChange = (event, value) => {
    setPage(value);
  };

  // Tab configuration - defines available tabs in clinical note editor
  const tabsArray = [
    "Session Note",      // Main clinical note with recommendations
    "Prescription",      // Medicine prescriptions and clinical examination
    "Follow-Up Summary", // Follow-up notes and care plan
    "Case History",      // Patient case history and background
  ];

  // Tab components mapping - defines which component renders for each tab
  const tabComponents = {
    "Session Note": {
      component: (
        <>
          <CNSessionNote />      {/* Main session note form */}
          <CNRecommendation />   {/* Recommendations section */}
        </>
      ),
      name: "Clinical Advice",  // Display name for the tab
    },
    Prescription: {
      component: <CNPrescription />,
      name: "Clinical Examination",
    },
    "Follow-Up Summary": {
      component: <CNFollowUpSummary />,
      name: "Follow-Up Note",
    },
    "Case History": {
      component: <CNCaseHistory />,
      name: "Case History",
    },
  };

  // Dynamic tab rendering - show all tabs in edit mode, otherwise filter by user's configuration
  const renderedTabs = expertClinicalNotesData?.editTemplate
    ? tabsArray
    : tabsArray.filter((tab) =>
      expertClinicalNotesData?.data?.tab?.includes(tab)
    );

  // Main component render
  return (
    <div className=" position-relative">
      {/* Header section with breadcrumbs, title, and action buttons */}
      <div
        style={{
          fontStyle: "normal",
          fontWeight: 700,
          fontSize: "22px",
          color: "#000000",
        }}
        className="d-flex align-items-md-center flex-column flex-md-row justify-content-md-between gap-2 "
      >
        {clinicalNotesState.view === "view" ? (
          <div role="presentation">
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                id="back_to_clinical_notes"
                onClick={() => navigate("/clinical/home")}
                role="button"
                sx={{
                  fontWeight: "500",
                  fontSize: "20px",
                  textDecoration: "none",
                }}
                className="d-flex text-secondary align-items-center justify-content-between gap-2 "
              >
                Clinical Co-Pilot
                <span
                  style={{
                    fontSize: "14px",
                    background: "#DCFAE6",
                    color: "#079455",
                    border: "1px solid #079455",
                  }}
                  className="  px-2  rounded-pill "
                >
                  Beta
                </span>
              </Link>

              {expertClinicalNotesData?.client?.name && (
                <Typography
                  sx={{ fontWeight: "500", fontSize: "20px" }}
                  color="text.primary"
                >
                  {expertClinicalNotesData?.client?.name}
                </Typography>
              )}
            </Breadcrumbs>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-between gap-2 ">
            <Link
              id="back_to_clinical_notes"
              color="text.primary"
              sx={{
                fontWeight: "500",
                fontSize: "20px",
                textDecoration: "none",
              }}
              role="button"
              onClick={() => navigate("/clinical/home")}
              className="d-flex my-3 align-items-center justify-content-between gap-2 "
            >
              Clinical Co-Pilot{" "}
              <span
                style={{
                  fontSize: "14px",
                  background: "#DCFAE6",
                  color: "#079455",
                  border: "1px solid #079455",
                }}
                className="  px-2  rounded-pill "
              >
                Beta
              </span>
            </Link>
          </div>
        )}
        {
          <>

            <div
              style={{ zIndex: "200", background: "white", width: "100vw" }}
              className="p-2 d-flex d-md-none justify-content-center position-fixed bottom-0 border-top border-2 translate-middle-x start-50"
            >
              {!expertClinicalNotesData?.data?.submitted &&
                expertClinicalNotesData?.headerData?.user_id &&
                clinicalNotesState.view === "view" ? (
                <div className=" d-flex clinicalnotes-submit-save justify-content-md-between gap-2 ">
                  <Actions downloadPDF={() => setPdfView(true)} />
                  <Button
                    className="save "
                    onClick={saveSession}
                    disabled={expertClinicalNotesData?.editTemplate}
                  >
                    <SaveOutlined sx={{ color: "var(--primary-color)" }} />
                    Save
                  </Button>
                  <Button
                    disabled={expertClinicalNotesData?.editTemplate}
                    id="submit_clinical_note"
                    className="PrimaryCTA "
                    onClick={() => setSubmission(true)}
                  >
                    Submit
                    <SendOutlined />
                  </Button>
                </div>
              ) : (
                expertClinicalNotesData?.data?.submitted &&
                expertClinicalNotesData?.headerData?.user_id && (
                  <>
                    <Actions downloadPDF={() => setPdfView(true)} />
                  </>
                )
              )}
            </div>
            <div className="d-none d-md-block">
              {!expertClinicalNotesData?.data?.submitted &&
                expertClinicalNotesData?.headerData?.user_id &&
                clinicalNotesState.view === "view" ? (
                <div className=" d-flex clinicalnotes-submit-save justify-content-md-between gap-2 ">
                  <Actions downloadPDF={() => setPdfView(true)} />
                  <Button
                    id="save_clinical_note"
                    className="save "
                    onClick={saveSession}
                    disabled={expertClinicalNotesData?.editTemplate}
                  >
                    <SaveOutlined sx={{ color: "var(--primary-color)" }} />
                    Save
                  </Button>
                  <Button
                    id="submit_clinical_note"
                    className="PrimaryCTA "
                    onClick={() => setSubmission(true)}
                    disabled={expertClinicalNotesData?.editTemplate}
                  >
                    Submit
                    <SendOutlined />
                  </Button>
                </div>
              ) : (
                expertClinicalNotesData?.data?.submitted &&
                expertClinicalNotesData?.headerData?.user_id && (
                  <>
                    <Actions downloadPDF={() => setPdfView(true)} />
                  </>
                )
              )}
            </div>
          </>
        }
      </div>
      {clinicalNotesState.view === "default" ? (
        <>
          <div className="d-flex align-items-lg-center justify-content-between flex-column flex-lg-row gap-2 ">
            <div>
              <ClinicalNoteStatus
                session={session}
                setSession={(data) => {
                  setPage(1);
                  setSession(data);
                }}
              />
            </div>

            <div
              style={{
                display: `${clientsList?.length === 0 ? "none" : "block"}`,
              }}
              className="create-note-button"
            >
              <CreateClinicalNote
                top={true}
                clientsList={clientsList?.length}
                setNoteType={(data) => setNoteType(data)}
                setAppointmentIDfromAPI={setAppointmentIDfromAPI}
                setClinicalNotesState={setClinicalNotesState}
                fetchClinicalNotesList={fetchClinicalNotesList}
              />
            </div>
          </div>

          {loading ? (
            <div
              className="row rows row-cols-1 border row-cols-md-2 row-cols-xl-3 row-cols-xxl-4  p-md-3 mx-1 mt-4 rounded-4  "
              style={{ background: "white" }}
            >
              {Array.from(new Array(8)).map((_, index) => (
                <ClientCardsSkeleton key={index} />
              ))}
            </div>
          ) : (
            <div>
              {clientsList?.length > 0 && (
                <>
                  <div
                    style={{
                      background: "#f9f9f9",
                    }}
                    className="row rows row-cols-1 border row-cols-md-2 row-cols-xl-3 row-cols-xxl-4  p-md-3 mx-1 mt-4 rounded-4 "
                  >
                    <>
                      {clientsList?.map((data, index) => (
                        <ClientsCard
                          setNoteType={(data) => setNoteType(data)}
                          key={index}
                          data={data}
                          handleView={(data) => {
                            setClinicalNotesState({ view: "view" });
                            setAppointmentIDfromAPI(data?._id);
                          }}
                          handleDelete={(data) =>
                            handleDeleteClinicalNote(data)
                          }
                        />
                      ))}
                    </>
                  </div>
                  <div className="d-flex justify-content-end mb-5 p-4">
                    <Box
                      sx={{
                        "& .MuiPaginationItem-root": {
                          "&.Mui-selected": {
                            backgroundColor: "var(--primary-color)", // Selected page background color
                            color: "#fff", // Selected page text color
                          },
                          "&:hover": {
                            backgroundColor: "var(--primary-color)", // Hover background color
                          },
                        },
                      }}
                    >
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handleChange}
                        renderItem={(item) => <PaginationItem {...item} />}
                      />
                    </Box>
                  </div>
                </>
              )}

              {clientsList?.length === 0 && (
                <div
                  style={{ height: "500px" }}
                  className="d-flex flex-column align-items-center justify-content-center  mx-1 mt-4 w-100 gap-4"
                >
                  {createNoteSVG}
                  <p
                    style={{
                      color: "#667085",
                      textAlign: "center",
                      fontFamily: "Roboto",
                      fontSize: "20px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "28px",
                      maxWidth: "500px",
                    }}
                  >
                    {session === "In Progress"
                      ? ` Get Started with Your First Clinical Note. Just Hit the Button Below!`
                      : `No ${session === "all" ? "" : session?.toLowerCase()
                      } notes found.`}
                  </p>
                  {session === "In Progress" && (
                    <CreateClinicalNote
                      clientsList={clientsList?.length}
                      setNoteType={(data) => setNoteType(data)}
                      setAppointmentIDfromAPI={setAppointmentIDfromAPI}
                      setClinicalNotesState={setClinicalNotesState}
                      fetchClinicalNotesList={fetchClinicalNotesList}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div
            className={`clinicalDashboard--scroll ${authorized?.view ? "" : "d-none"
              }`}
          >
            <UserInfoPanel
              sendPing={sendPing}
              loading={loading}
              downloadPDF={() => setPdfView(true)}
            />
            <UserSummaryPanel
              genSummary={fetchSummary}
              loading={loading}
              summaryLoading={summaryLoading}
            />
            {expertClinicalNotesData?.headerData?.user_id &&
              !expertClinicalNotesData?.editTemplate && (
                <div className=" sessionManagement--floatingContainer">
                  <FloatingService
                    screen="SessionNotes"
                    data={expertClinicalNotesData}
                  />
                </div>
              )}
            <PDFDialog open={pdfView} setOpen={(data) => setPdfView(data)} />
            <SaveUnsavedChanges onSave={saveSession} />
            <div
              className="mt-4 rounded rounded-4 position-relative border"
              style={{ backgroundColor: "white", overflowX: "auto" }}
            >
              <div className="SessionDashboard--TabsContainer px-md-4">
                <Tabs
                  value={selectedTab}
                  onChange={handleChangeTab}
                  indicatorColor="primary"
                  textColor="primary"
                  className="SessionDashboard--Tabs"
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                >
                  {renderedTabs.map((tab, index) => (
                    <Tab
                      key={index}
                      id={tab.replace(/\s+/g, "")} // Use a sanitized version of the tab name for id
                      label={tabComponents[tab].name} // Corrected to fetch the name from the tabComponents object
                    />
                  ))}
                </Tabs>
              </div>

              {renderedTabs.map((tab, index) => (
                <CustomTabPanel key={index} value={selectedTab} index={index}>
                  <div
                    style={{ paddingBottom: "100px" }}
                    className="SessionTab--Panel p-2 p-md-4 py-4 SessionSessionNote--Panel"
                  >
                    {tabComponents[tab].component}
                  </div>
                </CustomTabPanel>
              ))}
            </div>

            <div style={{ height: "100px" }}></div>
          </div>
          <div
            style={{ height: "100vh" }}
            className={`w-100  d-flex flex-column gap-5 justify-content-center  align-items-center
           ${authorized?.view ? "d-none" : ""}`}
          >
            <img width={200} src="/assets/img/no-worksheets.png" />
            <p
              style={{
                color: "var(--Text-text-secondary, #252422)",
                textAlign: "center",
                fontFamily: "Roboto",
                fontSize: "22px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "28px",
                maxWidth: "800px",
              }}
            >
              {authorized?.message}
            </p>
            <div className="w-100 d-flex justify-content-center gap-2 gap-md-4   ">
              <Button
                className="PrimaryCTA"
                onClick={() => {
                  setAuthorized({ view: true, message: "" });
                  navigate("/clinical/home");
                }}
                id="GoToClinicalCoPilot"
              >
                Go to Clinical Co-Pilot
              </Button>
              <Button
                className="PrimaryCTA--Outlined"
                onClick={() => {
                  setAuthorized({ view: true, message: "" });
                  navigate("/clinical/help-center");
                }}
                id="GoToHelpCenter"
              >
                Contact Support
              </Button>
            </div>
          </div>
        </>
      )}

      <ClinicalNoteSubmitDialog
        submission={submission}
        setSubmission={() => {
          setSubmission(false);
        }}
        handleSave={() => {
          saveSession();
          setSubmission(false);
          setClinicalNotesState((prevState) => ({
            ...prevState,
            view: "default",
          }));
        }}
        handleSubmit={() => {
          setSubmission(false);
          handleSubmit();
        }}
      />
    </div>
  );
};

export default ExpertClinicalNotesDB;
